import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Typography, Button, Card, CardContent, Alert } from '@mui/material';
import { ErrorOutline, Refresh, BugReport } from '@mui/icons-material';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  showDetails?: boolean;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorId?: string;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Generate error ID for user reference
    const errorId = this.generateErrorId();

    // Update state with error info
    this.setState({
      errorInfo,
      errorId,
    });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  private generateErrorId(): string {
    // Generate a simple error ID for user support
    const timestamp = Date.now().toString(36);
    const random = Math.random().toString(36).substring(2, 7);
    return `ERR-${timestamp}-${random}`.toUpperCase();
  }

  handleReload = () => {
    // Clear error state and reload the page
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: undefined,
    });

    window.location.reload();
  };

  handleRetry = () => {
    // Clear error state without reloading
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: undefined,
    });
  };

  handleReportBug = () => {
    // Open bug report or contact form
    const subject = encodeURIComponent(
      `Error Report: ${this.state.error?.message || 'Unknown Error'}`
    );
    const body = encodeURIComponent(
      `Error ID: ${this.state.errorId || 'N/A'}\n` +
        `URL: ${window.location.href}\n` +
        `Error: ${this.state.error?.message || 'Unknown Error'}\n` +
        `Stack: ${this.state.error?.stack || 'No stack trace'}\n\n` +
        `Please describe what you were doing when this error occurred:`
    );

    // You can customize this URL to your support system
    window.open(`mailto:support@lecturo.no?subject=${subject}&body=${body}`);
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
          padding={3}
        >
          <Card
            elevation={3}
            sx={{
              maxWidth: 600,
              width: '100%',
              textAlign: 'center',
            }}
          >
            <CardContent sx={{ padding: 4 }}>
              <ErrorOutline
                sx={{
                  fontSize: 64,
                  color: 'error.main',
                  marginBottom: 2,
                }}
              />

              <Typography variant="h5" gutterBottom color="error">
                Oops! Something went wrong
              </Typography>

              <Typography variant="body1" color="text.secondary" paragraph>
                We&apos;re sorry, but something unexpected happened. The error has been
                automatically reported to our team.
              </Typography>

              {this.state.errorId && (
                <Alert severity="info" sx={{ marginBottom: 2, textAlign: 'left' }}>
                  <Typography variant="caption">Error ID: {this.state.errorId}</Typography>
                </Alert>
              )}

              <Box display="flex" gap={2} justifyContent="center" marginTop={3}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={this.handleRetry}
                  startIcon={<Refresh />}
                >
                  Try Again
                </Button>

                <Button variant="outlined" onClick={this.handleReload} startIcon={<Refresh />}>
                  Reload Page
                </Button>

                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={this.handleReportBug}
                  startIcon={<BugReport />}
                >
                  Report Bug
                </Button>
              </Box>

              {/* Development mode: Show error details */}
              {this.props.showDetails && this.state.error && (
                <Box marginTop={3} padding={2} bgcolor="grey.100" borderRadius={1} textAlign="left">
                  <Typography variant="subtitle2" gutterBottom>
                    Error Details (Development Mode):
                  </Typography>
                  <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
                    {this.state.error.stack}
                  </Typography>

                  {this.state.errorInfo?.componentStack && (
                    <>
                      <Typography variant="subtitle2" gutterBottom sx={{ marginTop: 2 }}>
                        Component Stack:
                      </Typography>
                      <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
                        {this.state.errorInfo.componentStack}
                      </Typography>
                    </>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      );
    }

    // No error, render children normally
    return this.props.children;
  }
}

// Higher-order component for easier usage
// eslint-disable-next-line react-refresh/only-export-components -- HOC must be co-exported with ErrorBoundary class
export const withErrorBoundary = <P extends object>(
  Component: React.ComponentType<P>,
  fallback?: ReactNode,
  onError?: (error: Error, errorInfo: ErrorInfo) => void
): React.FC<P> => {
  const WrappedComponent: React.FC<P> = (props) => (
    <ErrorBoundary fallback={fallback} onError={onError}>
      <Component {...props} />
    </ErrorBoundary>
  );

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;

  return WrappedComponent;
};

// Export default ErrorBoundary
export default ErrorBoundary;
